import HttpClient from './HttpClient';
import { PartIdCount, SparePart, SparePartStatus } from '../interfaces/Item';
import { StatusType } from '../interfaces/Cart';

export interface CityResponseType {
  areaCode: string;
  city: string;
  cityValue: string;
}
export interface DistrictResponseType {
  city: string;
  cityValue: string;
  district: string; // the text label in {cc} language
  districtValue: string; // the value in english
}
export interface SearchSparePart {
  id: number;
  itemId: number;
  partName: string;
  partDescription: string;
  categoryId: string;
  subCategoryId: string;
  replacementId: string;
  stock: number;
  status: SparePartStatus;
  value: number;
  currency: string;
}
export interface OrderStatus {
  cart: {
    cartId: string;
    cartItems: PartIdCount[];
  };
  status: StatusType;
}
export function getImageSrc(id: number | string): string {
  const itemId = parseInt(id.toString(), 10);
  return `https://www.ikea.com/global/assets/customer-service/returns-claims/spareparts/images/${itemId}.png`;
}
export function safeParam(s: string): string {
  return s
    ? encodeURI(s.includes('/') ? s.substring(0, s.indexOf('/')) : s)
    : '';
}
export function convertBEToFESparePart(searchItem: SearchSparePart): SparePart {
  return {
    id: searchItem.itemId,
    replacementId: searchItem.replacementId,
    stock: searchItem.stock,
    description: searchItem.partDescription,
    // TODO: Replace with CSS config
    imageSrc: getImageSrc(searchItem.itemId),
    name: searchItem.partName,
    status: searchItem.status,
    categoryId: searchItem.categoryId,
    subCategoryId: searchItem.subCategoryId,
  };
}
class SparePartService {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  public async searchSpareParts(searchFor: string): Promise<SparePart[]> {
    const search = safeParam(searchFor);
    if (!search) {
      return [];
    }
    const { result, error } = await this.http.get(`search/${search}`);

    if (error) {
      throw error;
    }

    return result.map(convertBEToFESparePart);
  }

  public async fetchSparePartId(partId: number | string): Promise<SparePart> {
    const { error, result } = await this.http.get(`part/${partId}`);

    if (error) {
      throw error;
    }

    return convertBEToFESparePart(result);
  }

  public async fetchSparePartItemsFromArticleId(
    articleId: string
  ): Promise<SparePart[]> {
    const { error, result } = await this.http.get(`product/${articleId}`);

    if (error) {
      throw error;
    }

    return result.items.map(convertBEToFESparePart);
  }

  public async fetchSparePartRelatedToArticleId(
    articleId: string
  ): Promise<SparePart[]> {
    const { error, result } = await this.http.get(`products?ids=${articleId}`);

    if (error) {
      throw error;
    }

    return result;
  }

  public async fetchSparePartOrderId(
    orderId: string,
    token: string
  ): Promise<OrderStatus> {
    const { error, result } = await this.http.get(
      `order/status?order_id=${orderId}&token=${token}`
    );

    if (error) {
      throw error;
    }

    return result;
  }

  public async fetchSparePartConfirmOrderId(
    orderId: string,
    token: string
  ): Promise<OrderStatus> {
    const { error, result } = await this.http.get(
      `order/confirm?order_id=${orderId}&token=${token}`
    );

    if (error) {
      throw error;
    }

    return result;
  }

  public async fetchCitiesFromState(
    state: string
  ): Promise<CityResponseType[] | DistrictResponseType[]> {
    const { error, result } = await this.http.get(`address/${state}`);
    if (error) {
      throw error;
    }

    return result;
  }
}

export const sparePartService = new SparePartService(new HttpClient());
